import React from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/LocationPermissionModal.css';

/**
 * LocationPermissionModal Component
 *
 * Pre-prompt modal shown before requesting browser location permission.
 * Follows app standards: explain benefit before asking, provide "Not Now" option.
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Handler for "Not Now" (temporary dismiss)
 * - onEnable: function - Handler to trigger browser permission request
 * - isDenied: boolean - Whether permission was previously denied (shows settings instructions)
 */
function LocationPermissionModal({ isOpen, onClose, onEnable, isDenied }) {
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  // Detect platform for settings instructions
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="location-modal-overlay" onClick={onClose}>
      <div className="location-modal" onClick={(e) => e.stopPropagation()}>
        <button className="location-modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="location-modal-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>

        <h2 className="location-modal-title">
          {isDenied ? 'Location Disabled' : 'Enable Location'}
        </h2>

        <p className="location-modal-description">
          {isDenied
            ? 'Location access was denied. Enable it in your browser settings to see distances and get better recommendations.'
            : 'See how far you are from each place and get better recommendations nearby.'}
        </p>

        {isDenied ? (
          <div className="location-modal-settings-hint">
            <div className="settings-hint-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <p className="settings-hint-text">
              {isIOS
                ? 'Go to Settings → Safari → Location'
                : 'Tap the lock icon in your browser\'s address bar'}
            </p>
          </div>
        ) : (
          <div className="location-modal-benefits">
            <div className="location-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>See distances to places</span>
            </div>
            <div className="location-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Center map on your location</span>
            </div>
            <div className="location-benefit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Better nearby recommendations</span>
            </div>
          </div>
        )}

        <div className="location-modal-actions">
          {isDenied ? (
            <button className="location-modal-primary" onClick={onClose}>
              Got it
            </button>
          ) : (
            <button className="location-modal-primary" onClick={onEnable}>
              Enable Location
            </button>
          )}
          {!isDenied && (
            <button className="location-modal-secondary" onClick={onClose}>
              Not Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationPermissionModal;
